import { Data, List, Model, NeoModel } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { AppService, Types } from '../../../App/Services/AppService';
import FileDownloadApiClient from '../../../Common/ApiClients/FileDownloadApiClient';
import GeneralApiClient from '../../../Common/ApiClients/GeneralApiClient';
import FileDownloadComponentVM from '../../../Common/Components/FileDownload/FileDownloadComponentVM';
import StaticServiceProviderFileGroupingModel from '../../../Common/Models/StaticServiceProviderFileGroupingModel';
import ServiceProviderApiClient from '../../../Maintenance/ApiClients/ServiceProviderApiClient';
import DownloadHoldingsReportQuery from '../../../Models/FileDownload/DownloadHoldingsReportQuery';
import HoldingsApiClient from '../../ApiClients/HoldingsApiClient';
import CashHoldingLookup from '../Models/Lookups/CashHoldingLookup';
import SecurityHoldingLookup from '../Models/Lookups/SecurityHoldingLookup';
import SecurityHoldingSearchCriteria from '../Models/Queries/SecurityHoldingSearchCriteria';
import { StaticDataTypes } from '../../../Maintenance/StaticData/StaticDataTypes';
import InstrumentSearchLookup from '../../../DataInterception/InstrumentMapping/Models/InstrumentSearchLookup';
import ClientLookup from '../../../DataInterception/ClientAccountMapping/Models/ClientLookup';

@NeoModel
export default class HoldingsVM extends Views.ViewModelBase {

    public holdingsLookupCriteria = new SecurityHoldingSearchCriteria();
    public fileGroupingList = new List(StaticServiceProviderFileGroupingModel);
    public downloadHoldingsReportQuery = new DownloadHoldingsReportQuery();
    public holdingsApiClient = new HoldingsApiClient();
    public serviceProviderApiClient = new ServiceProviderApiClient();
    public generalApiClient = new GeneralApiClient();
    public fileDownloadVM = new FileDownloadComponentVM();
    public fileDownloadApiClient = new FileDownloadApiClient();

    public instruments: Model.PlainObject<InstrumentSearchLookup>[] = [];
    public filteredInstruments: Model.PlainObject<InstrumentSearchLookup>[] = [];
    public clientAccounts: Model.PlainObject<ClientLookup>[] = [];
    public filteredClientAccounts: Model.PlainObject<ClientLookup>[] = [];    

    public securityHoldingsPageManager = new Data.PageManager(this.holdingsLookupCriteria, SecurityHoldingLookup, this.holdingsApiClient.getSecurityHoldingPagedLists, {
        pageSize: 10,
        sortAscending: true,
        fetchInitial: false,
        initialTaskRunner: this.taskRunner,
    });

    public cashHoldingsPageManager = new Data.PageManager(this.holdingsLookupCriteria, CashHoldingLookup, this.holdingsApiClient.getCashHoldingPagedLists, {
        pageSize: 10,
        sortAscending: true,
        fetchInitial: false,
        initialTaskRunner: this.taskRunner,
    });

    constructor(
        public taskRunner = AppService.get(Types.Neo.TaskRunner),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService),
        private staticDataCache = AppService.get(StaticDataTypes.Services.StaticDataCache)) {
        super(taskRunner);
    }

    public async initialise() {
        await Promise.all([
            this.loadFileGroupings(),
            this.populateInstruments(),
            this.populateClientAccounts(),
        ]);
    }

    public async populateInstruments() {
        this.instruments = await this.staticDataCache.instruments.getDataAsync()
            .then((arr) => arr.sort((x, y) => x.description!.localeCompare(y.description!)));
        this.filteredInstruments = this.instruments.slice(0, 10);
    }

    public async populateClientAccounts() {
        this.clientAccounts = await this.staticDataCache.clientAccounts.getDataAsync()
            .then((arr) => arr.sort((x, y) => x.clientDescription.localeCompare(y.clientDescription)));
        this.filteredClientAccounts = this.clientAccounts.slice(0, 10);
    }

    public async loadFileGroupings() {
        const response = await this.taskRunner.waitFor(this.generalApiClient.getStaticServiceProviderFileGroupingList());
        this.fileGroupingList.set(response.data);
    }

    public async refreshPageManagers() {
        this.taskRunner.run(async () => {
            this.holdingsLookupCriteria.clientAccountNumber = this.holdingsLookupCriteria.clientAccountNumber.split(' | ')[0];
            await this.securityHoldingsPageManager.refreshData();
            await this.cashHoldingsPageManager.refreshData();
        });
    }

    public async downloadHoldingsFile() {
        await this.taskRunner.run(async () => {
            this.fileDownloadApiClient.downloadHoldingsReportFile(this.holdingsLookupCriteria.toJSObject());
        });
    }
}